import moment from 'moment';

import { piivoDateTime } from '../../../core/filters';
import i18n from '../../../core/i18n';
import { convertByteToMo } from '../../common/helpers/fileHelpers';

export interface Info {
  alias: string[];
  property: string;
  labelKey: string;
}
/**
 * Properties definition to display data in panel with only one item
 */
export function singleItemTabDefinition(): Info[] {
  return [
    {
      alias: ['name'],
      property: 'name',
      labelKey: 'dam.files.panel_selection.attributes.details.labels.name',
    },
    {
      alias: ['itemId'],
      property: 'itemId',
      labelKey: 'dam.files.panel_selection.attributes.details.labels.guid',
    },
    {
      alias: ['description', 'extension'],
      property: 'extension',
      labelKey: 'dam.files.panel_selection.attributes.details.labels.type',
    },
    {
      alias: ['size'],
      property: 'size',
      labelKey: 'dam.files.panel_selection.attributes.details.labels.size_file',
    },
    {
      alias: ['createdDate'],
      property: 'createdDate',
      labelKey: 'dam.files.panel_selection.attributes.details.labels.created_date',
    },
    {
      alias: ['updatesDate'],
      property: 'updateDate',
      labelKey: 'dam.files.panel_selection.attributes.details.labels.updates_date',
    },
    {
      alias: ['description', 'colorProfile'],
      property: 'colorProfile',
      labelKey: 'dam.files.panel_selection.attributes.details.labels.color_profile',
    },
    {
      alias: [],
      property: 'sizePx',
      labelKey: 'dam.files.panel_selection.attributes.details.labels.size_px',
    },
    {
      alias: ['description', 'horizontalResolution'],
      property: 'horizontalResolution',
      labelKey: 'dam.files.panel_selection.attributes.details.labels.horizontal_resolution',
    },
    {
      alias: [],
      property: 'sizeIn',
      labelKey: 'dam.files.panel_selection.attributes.details.labels.size_in',
    },
    {
      alias: [],
      property: 'sizeCm',
      labelKey: 'dam.files.panel_selection.attributes.details.labels.size_cm',
    },
  ];
}

export interface MultiInfo extends Info {
  merge?: string;
}

export function multipleItemTabDefinition(): MultiInfo[] {
  return [
    {
      alias: ['description', 'extension'],
      property: 'extension',
      labelKey: 'dam.files.panel_selection.attributes.details.labels.type',
      merge: 'concat',
    },
    {
      alias: ['size'],
      property: 'size',
      labelKey: 'dam.files.panel_selection.attributes.details.labels.size_file',
    },
  ];
}

function isInvalidNumber(number: number) {
  return (
    isNaN(number) || number === Number.NEGATIVE_INFINITY || number === Number.POSITIVE_INFINITY
  );
}

/**
 * Utils function to replace jsonPath with values
 * @param object - items to replace properties path by values
 * @param arrayPath - path to replace with values
 * @returns the value
 */
function jsonPath(object: any, arrayPath: string[]) {
  let value = object;
  for (const path of arrayPath) {
    value = value[path];
  }
  return value;
}

function getFileResolution(file: any) {
  return jsonPath(file, ['description', 'horizontalResolution']) || 0;
}

function getFileWidthInches(file: any) {
  return (jsonPath(file, ['description', 'width']) || 0) / getFileResolution(file);
}

function getFileHeightInches(file: any) {
  return (jsonPath(file, ['description', 'height']) || 0) / getFileResolution(file);
}

/**
 * Computed items infos with items as array and info as object
 * @param items - items to computed their properties
 * @param info - object contains computed definition
 * @returns items computed
 */
export function computeFileInfos(items: any[], info: Info): string {
  if (items.length === 1) {
    let valueProp = jsonPath(items[0], info.alias);

    switch (info.property) {
      case 'size':
        valueProp = convertByteToMo(valueProp) + ' Mo';
        return valueProp;
      case 'updateDate':
      case 'createdDate':
        valueProp = piivoDateTime(valueProp);
        if (valueProp === undefined) {
          return i18n.t('dam.files.panel_selection.attributes.details.size_unknown') as string;
        }
        return valueProp;
      case 'colorProfile':
        if (valueProp === undefined) {
          return i18n.t('dam.files.panel_selection.attributes.details.size_unknown') as string;
        }
        return valueProp;
      case 'sizePx':
        const widthPx = jsonPath(items[0], ['description', 'width']);
        const heightPx = jsonPath(items[0], ['description', 'height']);

        if (widthPx === undefined || heightPx === undefined) {
          return i18n.t('dam.files.panel_selection.attributes.details.size_unknown') as string;
        }

        return `${widthPx}x${heightPx}`;
      case 'horizontalResolution':
        if (valueProp === undefined) {
          return i18n.t('dam.files.panel_selection.attributes.details.size_unknown') as string;
        }
        return valueProp;
      case 'sizeIn':
        const widthIn = getFileWidthInches(items[0]);
        const heightIn = getFileHeightInches(items[0]);

        if (isInvalidNumber(widthIn) || isInvalidNumber(heightIn)) {
          return i18n.t('dam.files.panel_selection.attributes.details.size_unknown') as string;
        }

        return `${widthIn.toFixed(2)}x${heightIn.toFixed(2)}`;
      case 'sizeCm':
        const widthCm = 2.54 * getFileWidthInches(items[0]);
        const heightCm = 2.54 * getFileHeightInches(items[0]);

        if (isInvalidNumber(widthCm) || isInvalidNumber(heightCm)) {
          return i18n.t('dam.files.panel_selection.attributes.details.size_unknown') as string;
        }

        return `${widthCm.toFixed(2)}x${heightCm.toFixed(2)}`;
      default:
        break;
    }
    return valueProp;
  } else if (items.length > 0) {
    const valuesProp = [];
    for (const item of items) {
      valuesProp.push(jsonPath(item, info.alias));
    }
    let valueProp = '';
    switch (info.property) {
      case 'size':
        valueProp =
          convertByteToMo(
            valuesProp.reduce((accumulator, currentValue) => accumulator + currentValue)
          ) + ' Mo';
        break;
      case 'extension':
        valueProp = [...new Set(valuesProp)].join(', ');
        break;
      case 'updateDate':
      case 'createdDate':
        valueProp = valuesProp
          .map(strDate => moment(strDate))
          .sort((a, b) => {
            if (a.isBefore(b)) {
              return -1;
            }
            if (a.isAfter(b)) {
              return 1;
            }
            return 0;
          })
          .map(momentDate => momentDate.format('DD/MM/YYYY'))
          .join(', ');
        break;
      default:
        break;
    }
    return valueProp;
  }

  return '';
}
